<script setup lang="ts" generic="T extends Entity">
import { onMounted, ref, watch, computed } from "vue";
import type { FormKitSchemaFormKit } from "@formkit/core";
import { FormKit, FormKitSchema } from "@formkit/vue";
import { createZodPlugin } from "@formkit/zod";
import axios from "src/api/mutator/axios";
import { format } from "date-fns";
import { useToast } from "primevue/usetoast";
import { SchemaType } from "src/enums/schema-types";
import type { Entity } from "src/types/entities/entity-type";
import type { EntitySchema } from "src/types/entity-schema";
import Drawer from "primevue/drawer";
import SharedResizableDrawer from "./SharedResizableDrawer.vue";
import SharedLoadingIndicator from "./SharedLoadingIndicator.vue";

const visible = defineModel<boolean>("visible");

const { schema, type, data } = defineProps<{
  schema: EntitySchema;
  type: SchemaType;
  data: T | null;
}>();

const emits = defineEmits<{
  submit: [];
}>();

const toast = useToast();
const request = getRequest();
const fetchOneRequest = schema.requests.fetchOne;
const { mutateAsync } = request();
const {
  data: fetchOneData,
  isError,
  error,
  refetch: refetchSingleEntity,
  isPending: isFetchingSingleEntity,
} = fetchOneRequest(computed(() => data?.id));

const isLoading = computed(() => {
  if (type === SchemaType.CREATE) {
    return isFetchingSchemaOptions.value;
  }
  return isFetchingSchemaOptions.value || isFetchingSingleEntity.value;
});

const localData = ref<T | null>(null);
const errorMessage = ref<string | null>(null);
const schemaOptions = ref<FormKitSchemaFormKit>();
const isFetchingSchemaOptions = ref(true);
const refreshKey = ref(0);
const [zodPlugin, submitHandler] = createZodPlugin(getValidationSchema(), async (formData: T) => {
  if (!request) {
    throw new Error("Invalid request");
  }

  try {
    const serializedData = serializeData(formData);

    if (type === SchemaType.CREATE) {
      await mutateAsync({ data: serializedData });
    } else {
      await mutateAsync({ id: data?.id, data: serializedData });
    }

    toast.add({
      severity: "success",
      summary: "Success",
      detail: type === SchemaType.CREATE ? "Successfully created" : "Successfully saved",
      life: 3000,
    });

    emits("submit");

    visible.value = false;
  } catch (error: any) {
    errorMessage.value = error.response.data.detail;
  }
});

function deserializeData(rawData: T): T {
  const deserialized = { ...rawData };
  const formFields = schema.formSchema[type];
  if (!formFields) return deserialized;

  formFields.forEach((field: any) => {
    const key = field.name as keyof T;

    if (field.dateOutputFormat && typeof deserialized[key] === "string") {
      const value = deserialized[key] as string;
      if (!value.trim()) {
        (deserialized[key] as any) = null;
      } else {
        const parsed = new Date(value.replace(" ", "T"));
        if (!isNaN(parsed.getTime())) {
          (deserialized[key] as any) = parsed;
        }
      }
    }

    if (field.$formkit === "selectEntity" && deserialized[key] === undefined) {
      const objectKey = (field.name as string).replace(/_id$/, "") as keyof T;
      const obj = deserialized[objectKey];
      if (obj && typeof obj === "object" && (obj as any).id) {
        (deserialized[key] as any) = (obj as any).id;
      }
    }
  });

  return deserialized;
}

function serializeData(formData: T) {
  const serializedData = { ...formData };

  Object.keys(serializedData).forEach((key) => {
    const schemaOption = schema.formSchema[type].find((field: any) => field.name === key);

    if (schemaOption?.dateOutputFormat) {
      const formattedDate = format(serializedData[key as keyof T] as Date, schemaOption.dateOutputFormat).toString();

      (serializedData[key as keyof T] as any) = formattedDate;
    }
  });

  return serializedData;
}

async function fetchFormKitSchemaOptions() {
  const promises = schema.formSchema[type].map(async (field: any) => {
    if (field.optionsSrc) {
      try {
        const { data: options } = await axios.post(field.optionsSrc);
        const optionLabel = field.optionLabel || "name";
        const optionValue = field.optionValue || "id";

        field.options = options.data.map((option: any) => ({
          label: option[optionLabel],
          value: option[optionValue],
        }));
      } catch (error) {
        console.error(error);
      }
    }

    return field;
  });

  return Promise.all(promises);
}

function getRequest() {
  if (type === SchemaType.CREATE) {
    return schema.requests.create;
  }

  if (type === SchemaType.UPDATE) {
    return schema.requests.update;
  }

  if (type === SchemaType.VIEW) {
    return schema.requests.fetchOne;
  }

  throw new Error("Invalid request schema type");
}

function getValidationSchema() {
  if (type === SchemaType.CREATE) {
    return schema.validationSchema.create;
  }

  if (type === SchemaType.UPDATE) {
    return schema.validationSchema.update;
  }

  throw new Error("Invalid validation schema type");
}

watch(visible, async (newVisible) => {
  if (newVisible) {
    try {
      schemaOptions.value = await fetchFormKitSchemaOptions();
    } catch (error) {
      console.error(error);
    } finally {
      isFetchingSchemaOptions.value = false;
    }
  }
}, { immediate: true });

watch(
  () => data?.id,
  (currentId, newId) => {
    if (currentId !== newId) {
      refetchSingleEntity();
    }
  },
  {
    deep: true,
    immediate: true,
  }
);

watch(
  fetchOneData,
  (newData) => {
    if (newData) {
      const cloned = JSON.parse(JSON.stringify(newData));
      localData.value = deserializeData(cloned);
      refreshKey.value += 1;
    }
  },
  { deep: true, immediate: true }
);

watch(
  error,
  (newError) => {
    if (newError) {
      errorMessage.value = "Failed to load entity data";
    }
  },
  { immediate: true }
);
</script>

<template>
  <SharedResizableDrawer
    v-model:visible="visible"
    position="right"
    :header="`${type} ${schema?.entity}`"
    class="md:w-1/2 lg:w-1/3 xl:w-1/4"
  >
    <div v-if="visible && schemaOptions && !isLoading" :key="refreshKey">
      <FormKit
        id="form"
        v-model="localData"
        type="form"
        :submit-attrs="{
          inputClass: 'p-button p-component',
        }"
        :plugins="[zodPlugin]"
        @submit="submitHandler"
      >
        <FormKitSchema :schema="schemaOptions" :data="localData" />
      </FormKit>

      <div v-if="errorMessage" class="text-red-500 whitespace-pre-line">
        {{ errorMessage }}
      </div>
    </div>

    <div v-else-if="isLoading" class="flex justify-content-center mt-10">
      <SharedLoadingIndicator />
    </div>

    <div v-else-if="isError">
      <p>No schema loaded</p>
    </div>
  </SharedResizableDrawer>
</template>

<style scoped>
::v-deep(.p-select, .p-inputtext) {
  width: 100%;
}

::v-deep([data-type="primeCheckbox"]) .formkit-wrapper {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  min-height: 22px;
}

::v-deep([data-type="primeCheckbox"]) .formkit-label {
  order: 1;
  margin-bottom: 0;
  line-height: 1;
  display: flex;
  align-items: center;
}

::v-deep([data-type="primeCheckbox"]) .formkit-inner {
  order: 0;
  display: flex;
  align-items: center;
}

::v-deep([data-type="primeCheckbox"]) .p-checkbox {
  display: flex;
  align-items: center;
}
</style>
